import { EVENTS, subscribe } from 'ia-util-pubsub'
import { formatPrice } from 'ia-util-helpers'

// A rendered money string back to cents (`$48.00` -> 4800); null when it is not a price.
function toCents(text) {
  const number = parseFloat(String(text).replace(/[^0-9.]/g, ''))
  return Number.isNaN(number) ? null : Math.round(number * 100)
}

/**
 * Forms whose submit we already intercept. In memory, never a data attribute:
 * a CLONED card (the cart's recommendation strip rebuilds by cloning, so do
 * the filters and quick buy) inherits every attribute but none of the
 * listeners. A copy that looked "already bound" got no interceptor at all, and
 * its ADD TO BAG fell through to a native POST to /cart/add, which Shopify
 * answers with a redirect to /cart, which this theme redirects to the home
 * page. The shopper tapped add and landed on the home page.
 */
const boundForms = new WeakSet()

if (!customElements.get('ia-product-buy')) {
  class IaProductBuy extends HTMLElement {
    /* The parts are read on connect, never in the constructor: a constructor
       also runs for an element made with no parent and no children (a DOM
       recorder rebuilding the page node by node), and nothing is there yet. */
    bindParts() {
      this.form = this.querySelector('form')
      this.submitButton = this.querySelector('button[type="submit"]')
      this.submitButtonTextSpan = this.submitButton?.querySelector('span:not(.button__divider):not(.button__price-compare):not(.button__price)')
      this.textAddToCart = this.submitButton?.dataset.addToCartText || 'Add to Cart'
      this.textSoldOut = this.submitButton?.dataset.soldOutText || 'Sold Out'
      this.textUnavailable = this.submitButton?.dataset.unavailableText || 'Unavailable'
      this.cart = document.querySelector('ia-cart')
      this.variantInput = this.querySelector('input[name="id"]')
      this.quantityInput = this.querySelector('input[name="quantity"]')
      this.planInput = this.querySelector('input[name="selling_plan"]')
      this.limitNote = this.querySelector('[data-limit-message]')
      this.parentBlock = this.closest('ia-product-card') || this.closest('ia-pdp')
      // data-price-qty: the in-button price follows the picker (server markup is unit price, qty 1).
      this.priceContainer = this.hasAttribute('data-price-qty') ? this.querySelector('.ia-price') : null
      this.qty = 1
      this.captureUnitPrices()
    }

    connectedCallback() {
      this.bindParts()
      // Symmetric subscribe/unsubscribe. The old constructor-subscribe with
      // disconnect-unsubscribe went deaf permanently after one reconnect (the
      // cart drawer teleports itself to <body>, reconnecting every descendant).
      this.unsubscribers?.forEach((unsubscribe) => unsubscribe())
      this.unsubscribers = [
        subscribe(EVENTS.variantChange, this.handleVariantChange.bind(this)),
        subscribe(EVENTS.variantLoading, this.handleVariantLoading.bind(this)),
        subscribe(EVENTS.planChange, this.subscriptionChange.bind(this)),
        subscribe(EVENTS.cadenceChange, this.subscriptionChange.bind(this)),
      ]
      if (this.priceContainer) {
        this.unsubscribers.push(subscribe(EVENTS.qtyChange, this.handleQtyChange.bind(this)))
      }
      // The note appears on an add tap only; a bag edit that frees room clears it.
      if (this.limitNote) {
        this.unsubscribers.push(subscribe(EVENTS.cartChanged, () => {
          if (!this.atLimit()) this.showLimitNote(false)
        }))
      }

      // Guarded because the same reconnect stacked a second submit listener,
      // which added the product to the cart twice per click. The mark lives in
      // boundForms (see the top of the file), never in the markup.
      if (this.form && !boundForms.has(this.form)) {
        boundForms.add(this.form)
        this.form.addEventListener('submit', this.onSubmit.bind(this))
      }
    }

    disconnectedCallback() {
      this.unsubscribers?.forEach((unsubscribe) => unsubscribe())
      this.unsubscribers = null
    }

    get sectionId() {
      return this.getAttribute('section-id')
    }

    get productId() {
      return this.getAttribute('product-id')
    }

    handleVariantLoading(event) {
      const { productId, sectionId, loading } = event.detail
      if (productId == this.productId && sectionId == this.sectionId && this.submitButton) {
        if (loading) {
          this.submitButton.classList.add('loading')
        } else {
          this.submitButton.classList.remove('loading')
        }
      }
    }

    handleVariantChange(event) {
      const { productId, sectionId, variant } = event.detail
      if (this.variantInput && productId == this.productId && sectionId == this.sectionId) {
        if (variant) {
          this.variantInput.value = variant.id
          if (this.submitButtonTextSpan) this.submitButtonTextSpan.textContent = variant.available ? this.textAddToCart : this.textSoldOut
        } else {
          this.variantInput.value = ''
          if (this.submitButtonTextSpan) this.submitButtonTextSpan.textContent = this.textUnavailable
        }
        this.showLimitNote(false)
      }
    }

    // The picker shares the form id (its data-id), so other pickers on the page are ignored.
    handleQtyChange(event) {
      if (event.detail?.id !== this.dataset.id) return
      this.qty = Number(event.detail.value) || 1
      this.syncPriceQty()
    }

    // The unit strings as rendered: Liquid at first, ia-pdp.js after a plan or cadence change.
    captureUnitPrices() {
      if (!this.priceContainer) return
      this.unitSale = this.priceContainer.querySelector('[data-sale-price]')?.textContent.trim() || ''
      this.unitCompare = this.priceContainer.querySelector('[data-sale-price-compare]')?.textContent.trim() || ''
    }

    syncPriceQty() {
      if (!this.priceContainer) return
      const write = (selector, unit) => {
        const element = this.priceContainer.querySelector(selector)
        const cents = toCents(unit)
        if (element && cents !== null) element.textContent = formatPrice(cents * this.qty, { stripZeroCents: false })
      }
      write('[data-sale-price]', this.unitSale)
      write('[data-sale-price-compare]', this.unitCompare)
    }

    // Units of the selected variant already in the bag, across its lines (plans included).
    heldQty() {
      const variantId = Number(this.variantInput?.value)
      return (window.iaCartAPI?.cart?.items || []).reduce(
        (total, item) => (item.variant_id === variantId ? total + item.quantity : total),
        0
      )
    }

    atLimit() {
      if (!this.limitNote) return false
      const max = parseInt(this.limitNote.dataset.maxQty, 10) || Infinity
      return this.heldQty() >= max
    }

    showLimitNote(show) {
      this.limitNote?.classList.toggle('ia-hidden', !show)
    }


    async onSubmit (event) {
      event.preventDefault()

      if (this.submitButton.getAttribute('aria-disabled') === 'true') return

      // At the cap the tap gets the note instead of a request the bag should not take.
      if (this.atLimit()) {
        this.handleErrorMessage()
        this.showLimitNote(true)
        return
      }

      this.handleErrorMessage()
      this.submitButton.classList.add('adding')

      try {
        const ts = window.iaCartAPI?.getTimestamp()
        this.form.querySelectorAll('[data-property-ts]').forEach((input) => {
          input.value = ts
        })

        const formData = new FormData(this.form)

        if (this.parentBlock) {
          const planId = this.parentBlock.querySelector('[cadence="subscription"]')?.querySelector('[data-selling-plan-select]')?.value
          if (this.planInput && planId) {
            formData.append('selling_plan', planId)
          }
        }
        const response = await window.iaCartAPI?.addToCart(formData)
        this.handleSuccess(response)
      } catch (error) {
        this.handleError(error)
      } finally {
        this.submitButton.classList.remove('adding')
        this.submitButton.removeAttribute('aria-disabled')
      }
    }

    handleSuccess (data) {
      this.cart?.open(data)
    }

    handleError (error) {
      this.handleErrorMessage(error?.description || error.message || 'An error occurred')
    }

    handleErrorMessage (message = false) {
      const errorElement = this.querySelector('[data-error-message]')
      if (!errorElement) return

      errorElement.classList.toggle('ia-hidden', !message)
      if (message) {
        // Into the typed inner element, so the box's own font-size never applies to the text.
        const textElement = errorElement.querySelector('[data-error-text]') || errorElement
        textElement.textContent = message
      }
    }

    subscriptionChange(event) {
      const { productId, sectionId, cadence, plan } = event.detail
      if (productId == this.productId && sectionId == this.sectionId) {
        // A form without a selling_plan input has nothing to set
        if (!this.planInput) return
        this.planInput.value = ''
        if (cadence == 'subscription' && plan && plan.id) {
          this.planInput.value = plan.id
        }
        // ia-pdp.js rewrites the unit price on this same event; re-read it once every listener has run.
        if (this.priceContainer) {
          requestAnimationFrame(() => {
            this.captureUnitPrices()
            this.syncPriceQty()
          })
        }
      }
    }
  }

  customElements.define('ia-product-buy', IaProductBuy)
}
